/** Table state (page / size / sort / search / filters) persisted in the URL.
 *
 * Deviation from doc 01 §2: implemented on react-router's `useSearchParams`
 * rather than `nuqs`, to keep the Phase 1 dependency surface small. The hook API
 * is compatible, so swapping in nuqs later is a one-file change.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface TableState {
  page: number
  size: number
  sort?: string
  q?: string
  filters: Record<string, string[]>
}

const RESERVED = new Set(['page', 'size', 'sort', 'q'])

export function useTableState(defaults?: Partial<TableState>) {
  const [searchParams, setSearchParams] = useSearchParams()

  const state = useMemo<TableState>(() => {
    const filters: Record<string, string[]> = {}
    for (const key of new Set(searchParams.keys())) {
      if (RESERVED.has(key)) continue
      filters[key] = searchParams.getAll(key)
    }
    return {
      page: Number(searchParams.get('page') ?? defaults?.page ?? 1),
      size: Number(searchParams.get('size') ?? defaults?.size ?? 25),
      sort: searchParams.get('sort') ?? defaults?.sort ?? undefined,
      q: searchParams.get('q') ?? undefined,
      filters,
    }
  }, [searchParams, defaults?.page, defaults?.size, defaults?.sort])

  const setState = useCallback(
    (next: Partial<TableState>) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current)
          if (next.page !== undefined) params.set('page', String(next.page))
          if (next.size !== undefined) params.set('size', String(next.size))
          if (next.sort !== undefined) {
            next.sort ? params.set('sort', next.sort) : params.delete('sort')
          }
          if (next.q !== undefined) {
            next.q ? params.set('q', next.q) : params.delete('q')
            params.set('page', '1')
          }
          if (next.filters !== undefined) {
            for (const key of new Set(params.keys())) {
              if (!RESERVED.has(key)) params.delete(key)
            }
            for (const [key, values] of Object.entries(next.filters)) {
              for (const value of values) params.append(key, value)
            }
            params.set('page', '1')
          }
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const clearFilters = useCallback(() => {
    setState({ filters: {}, q: '' })
  }, [setState])

  const hasFilters = Boolean(state.q) || Object.keys(state.filters).length > 0

  return { state, setState, clearFilters, hasFilters }
}

/** Flatten table state into API query parameters. */
export function toQuery(state: TableState): Record<string, string | number | string[]> {
  const query: Record<string, string | number | string[]> = {
    page: state.page,
    size: state.size,
  }
  if (state.sort) query.sort = state.sort
  if (state.q) query.q = state.q
  for (const [key, values] of Object.entries(state.filters)) {
    if (values.length) query[key] = values
  }
  return query
}
